import { useLazyQuery } from "@apollo/client"
import {
  AssetEmissionsDocument,
  Emissions as EmissionsType,
  GetInstallationEmissionsDocument,
} from "@hydrologiq/gql/graphql"
import {
  formatYYYYMMDD,
  IANATimeZone,
  endOfDay,
  nowDateTime,
  previousDay,
  toUserTZ,
  startOfDay,
  toTimestamp,
} from "@hydrologiq/common/helpers/date"
import { EnergyPeriod } from "@hydrologiq/common/types/energy/consumption"
import { DateTime } from "timezonecomplete"
import Emissions from "@hydrologiq/shared/monitoring/figures/Emissions"
import { useEffect } from "react"

export interface InstallationEmissionsFactorProps {
  installationEndDate: DateTime
  assetId?: string
  title?: string
  installationId?: string
}

function InstallationEmissionsFactor({
  installationEndDate,
  assetId,
  title,
  installationId,
}: InstallationEmissionsFactorProps) {
  const dateNow = nowDateTime()
  const endOfInstallation = dateNow.unixUtcMillis() > installationEndDate.unixUtcMillis()
  const endDate = endOfInstallation ? installationEndDate : dateNow
  const prevDay = previousDay(endDate, endOfInstallation)

  const [
    getAssetEmissions,
    { loading: getAssetEmissionsLoading, error: getAssetEmissionsError, data: assetEmissionsData },
  ] = useLazyQuery(AssetEmissionsDocument)
  const [
    getInstallationEmissions,
    { loading: getInstallationEmissionsLoading, error: getInstallationEmissionsError, data: installationEmissionsData },
  ] = useLazyQuery(GetInstallationEmissionsDocument)

  const baseVars = {
    start: toTimestamp(startOfDay(prevDay), true),
    end: toTimestamp(endOfDay(prevDay), true),
    location: IANATimeZone(),
    compare: true,
  }

  useEffect(() => {
    if (assetId) {
      getAssetEmissions({
        variables: {
          ...baseVars,
          asset: assetId,
        },
      })
    } else if (installationId) {
      getInstallationEmissions({
        // @ts-ignore
        variables: {
          ...baseVars,
          installation: installationId,
        },
      })
    }
  }, [assetId, installationId])

  const data = assetId ? assetEmissionsData : installationEmissionsData
  const loading = assetId ? getAssetEmissionsLoading : getInstallationEmissionsLoading
  const error = assetId ? getAssetEmissionsError : getInstallationEmissionsError

  const dataPoint = (data && data.values && (data.values as EmissionsType[])) || undefined

  // If the previous day time is today then we set true
  let today =
    dataPoint && dataPoint.length > 0
      ? dataPoint?.[0]?.start && formatYYYYMMDD(toUserTZ(dataPoint[0].start)) === formatYYYYMMDD(endDate)
      : formatYYYYMMDD(endDate) === formatYYYYMMDD(dateNow)

  return (
    <Emissions
      title={title}
      loading={loading}
      emissions={dataPoint}
      period={today ? "today" : EnergyPeriod.PREV_DAY}
      error={error?.message}
    />
  )
}

export default InstallationEmissionsFactor
